import { useEffect, useState } from 'react';
import type { KeyboardEvent, MouseEvent, PointerEvent } from 'react';

import { useTranslation } from 'react-i18next';

import { AREA_SQUARE_MAX_LENGHT } from '@crm/entities/deal';
import CancelIcon from '@crm/shared/assets/icons/cancel-01.svg?react';
import FilterIcon from '@crm/shared/assets/icons/filter.svg?react';
import { DEFAULT_AREA_UNIT } from '@crm/shared/config';
import { tw } from '@crm/shared/lib';
import {
    Button,
    Field,
    FieldLabel,
    InputError,
    InputGroup,
    InputGroupInput,
    Popover,
    PopoverContent,
    PopoverTrigger,
    SvgIcon,
} from '@crm/shared/ui';

import { AREA_INPUT_VALUE_PATTERN } from './consts';

type TAreaInputType = 'from' | 'to';
type TAreaRange = Record<TAreaInputType, string>;

const parseAreaValue = (value: string) => Number(value.trim().replace(',', '.'));

export const ObjectAreaRange = ({
    value: areaValue,
    onChange,
    unit = DEFAULT_AREA_UNIT,
}: {
    value: TAreaRange;
    onChange: (value: TAreaRange) => void;
    unit?: string;
}) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [areaRange, setAreaRange] = useState<TAreaRange>(areaValue);

    const areaBadgeContainer = tw('flex items-center gap-1.5 rounded-sm bg-zinc-100 px-1 py-0.5');
    const areaBadgeStyles = tw('leading-none text-smtr-main-black-100');

    useEffect(() => {
        setAreaRange(areaValue);
    }, [areaValue]);

    const getNormalizedAreaRange = (range: TAreaRange): TAreaRange => ({
        from: range.from.trim(),
        to: range.to.trim(),
    });

    const hasAreaRangeValue = (range: TAreaRange) => range.from.length > 0 || range.to.length > 0;

    const normalizedAreaRange = getNormalizedAreaRange(areaRange);
    const areaFrom = parseAreaValue(normalizedAreaRange.from);
    const areaTo = parseAreaValue(normalizedAreaRange.to);
    const areaToError =
        normalizedAreaRange.from &&
        normalizedAreaRange.to &&
        Number.isFinite(areaFrom) &&
        Number.isFinite(areaTo) &&
        areaTo < areaFrom
            ? t('crm.postponedPage.filters.area.validation.toLessThanFrom')
            : undefined;

    const applyAreaRange = () => {
        if (areaToError) {
            return false;
        }

        const normalizedValue = getNormalizedAreaRange(areaValue);

        if (
            normalizedAreaRange.from === normalizedValue.from &&
            normalizedAreaRange.to === normalizedValue.to
        ) {
            return true;
        }

        if (hasAreaRangeValue(normalizedAreaRange) || hasAreaRangeValue(normalizedValue)) {
            onChange(normalizedAreaRange);
        }

        return true;
    };

    const onClearAreaRangePointerDown = (event: PointerEvent<HTMLSpanElement>) => {
        event.stopPropagation();
    };

    const onClearAreaRangeDate = (event: MouseEvent<HTMLSpanElement>) => {
        event.stopPropagation();
        setAreaRange({
            from: '',
            to: '',
        });
        onChange({
            from: '',
            to: '',
        });
    };

    const onInputChange = ({ type, inputValue }: { type: TAreaInputType; inputValue: string }) => {
        if (!AREA_INPUT_VALUE_PATTERN.test(inputValue)) {
            return;
        }

        setAreaRange((prev) => ({ ...prev, [type]: inputValue }));
    };

    const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();

            if (applyAreaRange()) {
                setIsOpen(false);
            }
        }
    };

    const onOpenChange = (open: boolean) => {
        setIsOpen(open);

        if (!open) {
            if (!applyAreaRange()) {
                setAreaRange(areaValue);
            }
        }
    };

    const appliedAreaRange = getNormalizedAreaRange(areaValue);
    const areaBadgeLabel = [appliedAreaRange.from, appliedAreaRange.to].filter(Boolean).join(' - ');

    return (
        <Popover open={isOpen} onOpenChange={onOpenChange}>
            <PopoverTrigger
                render={
                    <Button
                        variant="outline"
                        className="flex items-center justify-start gap-3.5 px-3"
                    >
                        <div className="flex items-center gap-2">
                            <SvgIcon icon={FilterIcon} />
                            <span className="text-smtr-main-black-100">
                                {t('crm.postponedPage.filters.area.label', { unit })}
                            </span>
                        </div>
                        {areaBadgeLabel ? (
                            <div className={areaBadgeContainer}>
                                <span className={areaBadgeStyles}>{areaBadgeLabel}</span>
                                <span
                                    onPointerDown={onClearAreaRangePointerDown}
                                    onClick={onClearAreaRangeDate}
                                >
                                    <SvgIcon icon={CancelIcon} size="16" />
                                </span>
                            </div>
                        ) : null}
                    </Button>
                }
            />
            <PopoverContent className="w-auto p-0" align="start">
                <div className="flex items-center gap-3 p-4">
                    <Field className="flex flex-col gap-2">
                        <FieldLabel htmlFor="areaFrom" className="text-smtr-main-tertiary-200">
                            {t('crm.postponedPage.filters.area.from', { unit })}
                        </FieldLabel>
                        <InputGroup>
                            <InputGroupInput
                                id="areaFrom"
                                placeholder="10"
                                className="w-19.5"
                                inputMode="decimal"
                                maxLength={AREA_SQUARE_MAX_LENGHT}
                                value={areaRange.from}
                                onChange={(event) =>
                                    onInputChange({
                                        type: 'from',
                                        inputValue: event.target.value,
                                    })
                                }
                                onKeyDown={onInputKeyDown}
                            />
                        </InputGroup>
                    </Field>
                    <span className="mt-7">-</span>
                    <Field className="flex flex-col gap-2">
                        <FieldLabel htmlFor="areaTo" className="text-smtr-main-tertiary-200">
                            {t('crm.postponedPage.filters.area.to', { unit })}
                        </FieldLabel>
                        <InputGroup>
                            <InputGroupInput
                                id="areaTo"
                                aria-invalid={Boolean(areaToError)}
                                placeholder="15"
                                className="w-19.5"
                                inputMode="decimal"
                                maxLength={AREA_SQUARE_MAX_LENGHT}
                                value={areaRange.to}
                                onChange={(event) =>
                                    onInputChange({
                                        type: 'to',
                                        inputValue: event.target.value,
                                    })
                                }
                                onKeyDown={onInputKeyDown}
                            />
                            {areaToError && (
                                <InputError
                                    errorMessage={areaToError}
                                    className="pr-2"
                                    tooltipSide="bottom"
                                />
                            )}
                        </InputGroup>
                    </Field>
                </div>
            </PopoverContent>
        </Popover>
    );
};
